import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DownloadsDoTabuleiro } from './componentes/BarraSuperior';
import type { ModoJogo } from '@core/tipos';
import { novaSemente } from '@core/aleatorio/rng';
import { corridaEmCurso, corridaPorComecar, nivelAtual, podeDesfazer } from '@core/motor/estados';
import { faseDoNivel } from '@core/modos/palavredo';
import { dataDoDia, respostaDoDia } from '@core/termo/dia';
import { estadoDasTeclas } from '@core/termo/avaliacao';
import { marcasDaCadeia, palavrasDoJogador } from '@core/termo/partida';
import { TAMANHO_MAX_JOGADOR } from '@core/lexico/alfabeto';
import { caracteres, tamanhoVisivel } from '@core/lexico/normalizacao';
import type { IndiceLexico } from '@core/lexico/indice';
import { MSG_SESSAO_RESTAURADA } from '@core/motor/mensagens';
import { regiaoAtiva } from '@core/tabuleiro/areaAtiva';
import type { Armazem } from '@persistencia/indexeddb';
import { chaveDaPartida } from '@persistencia/schema';
import { NOME_MODO } from '@core/motor/mensagens';
import { somDaMudanca, tocar } from './som';
import { nomeDoArquivo } from './imagem/baixar';
import { paletaDoDocumento } from './imagem/desenho';
import { baixarGifDoTabuleiro } from './imagem/tabuleiroGif';
import { baixarCanvas, desenharTabuleiro } from './imagem/tabuleiroPng';
import { AlternadorEncaixe } from './componentes/AlternadorEncaixe';
import { CampoDraft } from './componentes/CampoDraft';
import { ControlesZoom } from './componentes/ControlesZoom';
import { DialogoFase, DialogoFim, DialogoRecomecar } from './componentes/Dialogo';
import { LinhaFeedback } from './componentes/LinhaFeedback';
import { PainelModo } from './componentes/PainelModo';
import { RegiaoViva } from './componentes/RegiaoViva';
import { Tabuleiro } from './componentes/Tabuleiro';
import { TecladoVirtual, useTecladoVirtual } from './componentes/TecladoVirtual';
import { useCamera } from './ganchos/useCamera';
import { useJogo } from './ganchos/useJogo';

/**
 * O jogo em cadeia: Bistrô, Nitro e Átrio (§22).
 *
 * Os três são o mesmo jogo com outra cobrança, e por isso dividem esta tela e
 * o mesmo `EstadoJogo`. A Trama não entra aqui — é outro jogo, com
 * estado próprio (ADR-0009).
 */
export function JogoCadeia({
  indice,
  dicionarioPronto,
  armazem,
  modo,
  duracaoSegundos,
  pedidoDeRecomeco,
  aoContinuarNoZen,
  aoOferecerImagem,
  aoOferecerDesfazer,
}: {
  indice: IndiceLexico;
  dicionarioPronto: boolean;
  armazem: Armazem;
  modo: ModoJogo;
  /** Só o Nitro usa: quanto dura a partida, em segundos (§22.2). */
  duracaoSegundos?: number;
  /** Contador de cliques em `Recomeçar`, que fica na barra da casca. */
  pedidoDeRecomeco: number;
  /** §22.6: avisa a casca de que o modo em curso passou a ser o Bistrô. */
  aoContinuarNoZen: () => void;
  /**
   * §11: entrega à barra a ação de desfazer, ou `undefined` quando não há o
   * que desfazer. Mesmo arranjo de `aoOferecerImagem`, e pela mesma razão.
   */
  aoOferecerDesfazer: (acao: (() => void) | undefined) => void;
  /**
   * §7.5: entrega à barra a função que baixa a imagem do tabuleiro, ou
   * `undefined` enquanto não há tabuleiro nenhum para baixar.
   *
   * Os itens moram no painel do `i`, que é da casca, mas só o jogo conhece a
   * cadeia. Em vez de subir o estado inteiro, sobem funções — e a casca não
   * precisa saber nada sobre como a imagem é feita.
   */
  aoOferecerImagem: (baixar: DownloadsDoTabuleiro | undefined) => void;
}) {
  const tabuleiroRef = useRef<HTMLDivElement>(null);
  const campoRef = useRef<HTMLInputElement>(null);

  const camera = useCamera(tabuleiroRef);
  const tecladoVirtual = useTecladoVirtual();

  const jogo = useJogo({
    indice,
    dicionarioPronto,
    camera: camera.camera,
    armazem,
    modo,
    duracaoSegundos,
    sessaoVencida: (salvo) =>
      salvo.modo === 'termo' && salvo.dia !== undefined && salvo.dia !== dataDoDia(Date.now()),
  });

  const { estado, despachar } = jogo;

  const [posicaoCaret, setPosicaoCaret] = useState(0);
  const [recomecarAberto, setRecomecarAberto] = useState(false);
  /* O fim da partida abre sozinho; `Ver o tabuleiro` só fecha o diálogo, sem
     encerrar a partida — o tabuleiro acabado continua lá para ser percorrido. */
  const [fimDispensado, setFimDispensado] = useState(false);

  /*
   * O jogo só está utilizável quando o dicionário carregou **e** a leitura da
   * sessão terminou (§12): até lá o motor está em `carregando` e descarta
   * `editar-draft`. Habilitar o campo antes disso abre uma janela em que a
   * pessoa digita e o texto desaparece — visível em navegadores cuja leitura
   * do IndexedDB é mais lenta.
   */
  const carregando = !dicionarioPronto || estado.fase === 'carregando';

  /* §22.3: partida encerrada não recebe mais texto — nem do teclado do
     sistema, nem do teclado do jogo. */
  const escritaBloqueada = carregando || estado.fase === 'fim';

  /**
   * Devolve o foco ao campo depois de uma ação feita com o ponteiro.
   *
   * Todo controle do jogo é um `<button>`, e clicar num botão deixa o foco
   * nele. Quando o botão some — a letra-âncora vira draft, o diálogo fecha —
   * o foco cai no `body`, e as teclas seguintes não chegam a lugar nenhum: o
   * jogador digita e nada aparece. Escolher a letra e escrever a palavra são
   * um gesto só, então o campo é o destino natural do foco depois deles.
   *
   * O adiamento é necessário: fechar um `<dialog>` devolve o foco a quem o
   * abriu, e isso acontece **depois** do nosso `focus()`.
   */
  const focarCampo = useCallback(() => {
    requestAnimationFrame(() => campoRef.current?.focus());
  }, []);

  /* A barra pediu para recomeçar: abre a confirmação de T09. O primeiro valor
     do contador não conta, senão a página abriria já perguntando. */
  const recomecosVistos = useRef(pedidoDeRecomeco);
  useEffect(() => {
    if (pedidoDeRecomeco === recomecosVistos.current) return;
    recomecosVistos.current = pedidoDeRecomeco;
    setRecomecarAberto(true);
  }, [pedidoDeRecomeco]);

  /* T01-01: o campo recebe foco assim que o jogo fica utilizável. */
  useEffect(() => {
    if (!carregando) campoRef.current?.focus();
  }, [carregando]);

  /**
   * O som de cada movimento (§24).
   *
   * Sai da mudança do estado, e não dos manipuladores: assim o teclado do
   * sistema, o teclado do jogo, o desfazer e a colagem soam igual, sem que
   * nenhum caminho novo precise lembrar de tocar nada.
   *
   * A restauração é muda de propósito — a sessão salva chega com a cadeia
   * inteira de uma vez, e anunciá-la seria tocar uma jogada que ninguém fez
   * agora. Por isso a fase anterior tranca o primeiro par comparado.
   */
  const anteriorSonoro = useRef({
    draft: estado.draft,
    palavras: estado.cadeia.palavras.length,
    fase: estado.fase,
  });
  useEffect(() => {
    const anterior = anteriorSonoro.current;
    const atual = {
      draft: estado.draft,
      palavras: estado.cadeia.palavras.length,
      fase: estado.fase,
    };
    anteriorSonoro.current = atual;
    if (anterior.fase === 'carregando') return;

    const som = somDaMudanca(anterior, atual);
    if (som !== undefined) tocar(som);
  }, [estado]);

  /* Sair da fase final rearma o diálogo para a partida seguinte. */
  useEffect(() => {
    if (estado.fase !== 'fim') setFimDispensado(false);
  }, [estado.fase]);

  /**
   * Ações de jogo despachadas pela interface. Todas terminam com o jogador
   * prestes a digitar, então todas devolvem o foco ao campo.
   */
  const jogar = useCallback(
    (evento: Parameters<typeof despachar>[0]) => {
      despachar(evento);
      focarCampo();
    },
    [despachar, focarCampo],
  );

  /**
   * Escrita pelo teclado do jogo (§14).
   *
   * A tecla não escreve no campo: despacha o draft inteiro, como faria uma
   * digitação de verdade, e deixa `filtrarEntrada` decidir o que entra. Só o
   * cursor precisa de ajuda — o campo não recebeu `input`, então ninguém
   * moveria o caret. Escrevendo sempre no fim, a posição é o tamanho do texto.
   *
   * O texto de partida vem de um ref, e não de `estado.draft`: dois toques
   * dentro do mesmo lote de renderização leriam o mesmo draft antigo e o
   * segundo apagaria o primeiro. O ref se ressincroniza a cada render, então
   * o que o motor decidir continua sendo a verdade.
   */
  const draftRef = useRef(estado.draft);
  draftRef.current = estado.draft;

  const escreverNoDraft = useCallback(
    (transformar: (draft: string) => string) => {
      const texto = transformar(draftRef.current);
      draftRef.current = texto;
      despachar({ tipo: 'editar-draft', texto });
      setPosicaoCaret(Math.min(tamanhoVisivel(texto), TAMANHO_MAX_JOGADOR));
    },
    [despachar],
  );

  const aoLetraVirtual = useCallback(
    (letra: string) => escreverNoDraft((draft) => draft + letra),
    [escreverNoDraft],
  );

  const aoApagarVirtual = useCallback(
    () => escreverNoDraft((draft) => caracteres(draft).slice(0, -1).join('')),
    [escreverNoDraft],
  );

  /**
   * §11: desfazer pelo teclado do jogo.
   *
   * Despacha o mesmo evento que `Ctrl+Z` no campo, e por isso obedece à mesma
   * regra: com texto escrito desfaz o texto, com o campo vazio desfaz a jogada
   * (ADR-0006). Não mexe no `draftRef` de propósito — só o motor sabe em que
   * texto o desfazer vai parar, e chutar `''` aqui apagaria o draft que uma
   * jogada desfeita acabou de devolver. O ref se ressincroniza no render
   * seguinte, como sempre.
   */
  const desfazerVirtual = useCallback(() => despachar({ tipo: 'desfazer' }), [despachar]);

  /* §11: a mesma resposta serve à tecla do rodapé e ao botão da barra. */
  const desfazerHabilitado = !carregando && podeDesfazer(estado);

  /* T07-06: digitar continua indo para o draft depois de navegar. Clicar em
     área vazia do palco também traz o foco de volta, venha ele de onde vier. */
  const aoClicarTabuleiro = useCallback(
    (e: React.PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('button, input, a, summary')) return;
      focarCampo();
    },
    [focarCampo],
  );

  /* T04-07: a câmera acompanha a área ativa sem salto abrupto. */
  const area = regiaoAtiva(estado);
  const chaveArea = `${area.colunaMin},${area.linhaMin},${area.colunaMax},${area.linhaMax}`;
  const recentralizar = camera.recentralizar;
  useEffect(() => {
    recentralizar(area);
    // `chaveArea` é a identidade estável da região; `area` é recriada a cada
    // render. `recentralizar` muda quando a viewport muda (§14: girar o
    // aparelho ou abrir o teclado virtual também recentraliza).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveArea, recentralizar]);

  const comecarPartida = useCallback(
    (modo?: ModoJogo, duracaoSegundos?: number) => {
      // A partida que se apaga é a deste modo, e cada um mora na sua chave
      // (ADR-0009): recomeçar na Trama não pode levar junto a cadeia do Bistrô,
      // nem o contrário.
      void armazem.remover(chaveDaPartida(estado.modo));
      // Sem `modo`, o motor mantém o atual: recomeçar é "de novo, igual".
      despachar({
        tipo: 'recomecar',
        semente: novaSemente(Math.random),
        ...(modo === undefined ? {} : { modo }),
        ...(duracaoSegundos === undefined ? {} : { duracaoSegundos }),
      });
      focarCampo();
    },
    [armazem, despachar, focarCampo, estado.modo],
  );

  const esgotarTempo = useCallback(() => despachar({ tipo: 'tempo-esgotado' }), [despachar]);

  /**
   * §22.7: no arcade, `fim` é a pausa entre fases, e não o fim da partida.
   *
   * O tabuleiro continua na tela até o jogador pedir a fase seguinte — é ele
   * que decide quando o trabalho de cinco níveis pode ir embora.
   */
  const arcade = estado.modo === 'palavredo';
  const fimDeFase = arcade && estado.fase === 'fim';
  const faseAtual = faseDoNivel(nivelAtual(estado));

  const comecarFase = useCallback(() => {
    despachar({ tipo: 'comecar-fase' });
    setFimDispensado(false);
    focarCampo();
  }, [despachar, focarCampo]);

  /* §23.4: o que já se sabe de cada letra, para o teclado carregar. */
  const teclasDoDia = useMemo(() => {
    if (estado.modo !== 'termo' || estado.resposta === undefined) return undefined;
    const marcas = marcasDaCadeia(estado.cadeia, estado.resposta);
    return estadoDasTeclas(
      palavrasDoJogador(estado.cadeia).map((p) => ({
        texto: p.texto,
        letras: marcas.get(p.id) ?? [],
      })),
    );
  }, [estado.modo, estado.cadeia, estado.resposta]);

  /**
   * O dia da Trama (§23.2).
   *
   * Quem consulta o relógio e deriva a palavra é aqui: o núcleo não faz nem
   * uma coisa nem outra (ADR-0001). O dia é reavaliado de tempos em tempos
   * para a aba deixada aberta virar sozinha à meia-noite, e uma vez mais
   * quando a sessão salva chega, que é quando se descobre de que dia ela era.
   */
  const [instante, setInstante] = useState(() => Date.now());
  useEffect(() => {
    if (modo !== 'termo') return;
    const id = window.setInterval(() => setInstante(Date.now()), 30_000);
    return () => window.clearInterval(id);
  }, [modo]);

  useEffect(() => {
    if (modo !== 'termo' || !dicionarioPronto) return;
    const dia = dataDoDia(instante);
    // Já está no dia certo: nada a fazer. A comparação também é o que faz este
    // efeito acordar depois da restauração — a sessão salva traz o dia em que
    // ela foi jogada, e se for outro é aqui que o tabuleiro de ontem sai da
    // tela. Sem `estado.dia` nas dependências, quem abrisse a página no dia
    // seguinte veria o tabuleiro velho até o relógio de 30 s bater.
    if (estado.dia === dia) return;
    const resposta = respostaDoDia(indice, dia);
    if (resposta === undefined) return;
    despachar({ tipo: 'dia-pronto', dia, resposta });
  }, [modo, dicionarioPronto, indice, instante, despachar, estado.dia]);

  /**
   * O cronômetro do Nitro nasce na primeira letra, não na escolha do modo
   * (§22.2, T12-02): entre uma coisa e outra há o tempo de a pessoa se ajeitar,
   * e ele não é dela.
   *
   * O prazo é calculado aqui porque o relógio vive aqui — `src/core` não o
   * conhece (ADR-0001). Vai ao motor como um instante absoluto, o que faz o
   * tempo correr mesmo com a aba fechada: recarregar não devolve segundos.
   */
  useEffect(() => {
    if (!corridaPorComecar(estado) || estado.draft.length === 0) return;
    despachar({
      tipo: 'iniciar-corrida',
      terminaEm: Date.now() + (estado.duracaoSegundos ?? 0) * 1000,
    });
  }, [estado, despachar]);

  /**
   * Corrida restaurada cujo prazo já venceu (§12, T12-05). Acontece ao voltar
   * numa aba deixada aberta ou ao recarregar depois do tempo: o placar é o que
   * estava valendo quando o relógio bateu, e não se joga mais nele.
   */
  useEffect(() => {
    if (!corridaEmCurso(estado)) return;
    if (estado.terminaEm !== undefined && Date.now() >= estado.terminaEm) esgotarTempo();
  }, [estado, esgotarTempo]);

  /**
   * §22.6: seguir no mesmo tabuleiro depois de achar a palavra do dia.
   *
   * O motor troca o modo com a cadeia de pé e volta para `escolher-ancora`, que
   * é onde a jogada tinha parado; a casca acerta a barra e a preferência
   * salva. O armazém **não** é limpo — é justamente o tabuleiro que se quer.
   */
  const continuarNoZen = useCallback(() => {
    despachar({ tipo: 'continuar-cadeia', modo: 'zen' });
    aoContinuarNoZen();
    focarCampo();
  }, [despachar, aoContinuarNoZen, focarCampo]);

  /**
   * §7.5: o tabuleiro inteiro como PNG.
   *
   * O estado vem de um ref para a função ter identidade estável: ela viaja até
   * a barra e não pode mudar a cada tecla digitada, senão a casca renderizaria
   * de novo o jogo inteiro a cada letra.
   */
  const estadoRef = useRef(estado);
  estadoRef.current = estado;

  /**
   * O que os dois formatos precisam saber do estado.
   *
   * Só o nome do jogo e o do modo vão para o rodapé. O número do dia fica no
   * nome do arquivo, onde serve para ordenar; no rodapé ele viraria placar — e
   * §23.1 tirou o placar da Trama para a dedução não virar comparação.
   */
  const retratoDoTabuleiro = useCallback(() => {
    const atual = estadoRef.current;
    const marcasDoDia =
      atual.modo === 'termo' ? marcasDaCadeia(atual.cadeia, atual.resposta) : undefined;

    return {
      dia: atual.dia,
      opcoes: {
        cadeia: atual.cadeia,
        marcas: marcasDoDia,
        legenda: `Entreletras · ${NOME_MODO[atual.modo]}`,
        paleta: paletaDoDocumento(),
      },
    };
  }, []);

  const baixarImagem = useCallback(() => {
    const { dia, opcoes } = retratoDoTabuleiro();
    void baixarCanvas(desenharTabuleiro(opcoes), nomeDoArquivo(dia, 'png'));
  }, [retratoDoTabuleiro]);

  const baixarGif = useCallback(() => {
    const { dia, opcoes } = retratoDoTabuleiro();
    baixarGifDoTabuleiro(opcoes, nomeDoArquivo(dia, 'gif'));
  }, [retratoDoTabuleiro]);

  const temTabuleiro = estado.cadeia.palavras.length > 0;
  useEffect(() => {
    aoOferecerImagem(temTabuleiro ? { png: baixarImagem, gif: baixarGif } : undefined);
  }, [aoOferecerImagem, temTabuleiro, baixarImagem, baixarGif]);

  useEffect(() => {
    aoOferecerDesfazer(desfazerHabilitado ? desfazerVirtual : undefined);
  }, [aoOferecerDesfazer, desfazerHabilitado, desfazerVirtual]);

  const confirmarRecomecar = useCallback(() => {
    comecarPartida();
    setRecomecarAberto(false);
  }, [comecarPartida]);

  const cancelarRecomecar = useCallback(() => {
    setRecomecarAberto(false);
    focarCampo();
  }, [focarCampo]);

  return (
    <>
      <a className="pular-para-campo" href="#campo-draft">
        Ir para a palavra
      </a>

      <div className="palco" onPointerDown={aoClicarTabuleiro}>
        <Tabuleiro
          estado={estado}
          indice={indice}
          camera={camera.camera}
          animando={camera.animando}
          arrastando={camera.arrastando}
          viewport={camera.viewport}
          manipuladores={camera.manipuladores}
          elementoRef={tabuleiroRef}
          posicaoCaret={posicaoCaret}
          despachar={jogar}
        />

        <CampoDraft
          estado={estado}
          campoRef={campoRef}
          despachar={despachar}
          aoMoverCaret={setPosicaoCaret}
          desabilitado={escritaBloqueada}
          tecladoVirtual={tecladoVirtual}
        />

        <LinhaFeedback estado={estado} />

        {/* §22: o Bistrô não tem painel; os outros três têm o seu. */}
        <PainelModo estado={estado} aoEsgotarTempo={esgotarTempo} />

        {/* T06-04: qual ocorrência da âncora está ativa, com alternância. */}
        <AlternadorEncaixe estado={estado} despachar={jogar} />

        {/* §22.7: com a fase fechada não há palavra atual para voltar, e o
            lugar do botão flutuante passa a ser a saída da fase. */}
        {fimDeFase && fimDispensado && (
          <button
            type="button"
            className="botao botao--primario voltar-ativa"
            onClick={comecarFase}
          >
            Começar Fase {faseAtual + 1}
          </button>
        )}

        {/* T07-04: só aparece quando a área ativa saiu da viewport. */}
        {!fimDeFase && camera.precisaVoltar(area) && (
          <button
            type="button"
            className="botao botao--primario voltar-ativa"
            onClick={() => camera.recentralizar(area)}
          >
            Voltar à palavra atual
          </button>
        )}

        <ControlesZoom zoom={camera.camera.zoom} aoAjustar={camera.ajustarZoom} />

        {jogo.avisoPersistencia !== undefined && <p className="aviso">{jogo.avisoPersistencia}</p>}
      </div>

      {/* §14: irmão do palco, não sobreposto — o tabuleiro encolhe uma vez e
          nada mais cobre a palavra que está sendo escrita. */}
      {/* §14: o teclado do jogo aparece em aparelho de toque. §23.4: na Trama
          ele aparece sempre, porque deixou de ser só entrada — é nele que se
          lê o que já foi descoberto de cada letra. */}
      {(tecladoVirtual || estado.modo === 'termo') && (
        <TecladoVirtual
          aoLetra={aoLetraVirtual}
          aoApagar={aoApagarVirtual}
          aoDesfazer={desfazerVirtual}
          podeDesfazer={desfazerHabilitado}
          estadoDasLetras={teclasDoDia}
          desabilitado={escritaBloqueada || estado.fase === 'escolher-ancora'}
        />
      )}

      {/* §12: a restauração é anunciada, sem modal. */}
      <RegiaoViva mensagem={jogo.restaurada ? MSG_SESSAO_RESTAURADA : estado.anuncio} />

      <DialogoFase
        aberto={fimDeFase && !fimDispensado}
        fase={faseAtual}
        proximaFase={faseAtual + 1}
        baixar={temTabuleiro ? { png: baixarImagem, gif: baixarGif } : undefined}
        aoVerTabuleiro={() => setFimDispensado(true)}
        aoComecar={comecarFase}
      />

      <DialogoFim
        aberto={!arcade && estado.fase === 'fim' && !fimDispensado}
        estado={estado}
        aoVerTabuleiro={() => setFimDispensado(true)}
        aoJogarDeNovo={() => comecarPartida()}
        aoContinuar={continuarNoZen}
      />

      <DialogoRecomecar
        aberto={recomecarAberto}
        aoCancelar={cancelarRecomecar}
        aoConfirmar={confirmarRecomecar}
      />
    </>
  );
}
